import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import ClientLayout from '../../../components/layout/ClientLayout';
import { useEmailVerification } from '../../../hooks/useEmailVerification';
import { useAuth } from '../../../hooks/useAuth';

const CodeApplyPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [hasApplications, setHasApplications] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTime, setBlockTime] = useState<number | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { loading: emailVerificationLoading } = useEmailVerification();

  useEffect(() => {
    const checkExistingApplications = async () => {
      // authLoading이 완료될 때까지 대기
      if (authLoading) {
        return;
      }

      // 인증 로딩이 완료된 후 user가 없으면 로그인 페이지로 이동
      if (!user) {
        navigate('/signin');
        return;
      }

      try {
        setLoading(true);

        // 사용자의 대기중인 신청서 확인
        const applicationsRef = collection(db, 'portville_applications');
        const q = query(
          applicationsRef,
          where('uid', '==', user.uid),
          where('status', '==', 'pending')
        );
        
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          setHasApplications(true);
          // 이미 대기중인 신청이 있으므로 DetailPage로 이동
          navigate('/portville/detail');
        } else {
          setHasApplications(false);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('신청 확인 중 오류 발생:', err);
        setError('신청 내역을 확인하는 중 오류가 발생했습니다.');
        setLoading(false);
      }
    };

    // 로컬 스토리지에서 차단 정보 확인
    const blockedUntil = localStorage.getItem('codeApplyBlockedUntil');
    const attemptCount = localStorage.getItem('codeApplyAttempts');
    
    if (blockedUntil) {
      const blockUntilTime = parseInt(blockedUntil, 10);
      if (blockUntilTime > Date.now()) {
        // 아직 차단 시간이 지나지 않음
        setIsBlocked(true);
        setBlockTime(Math.ceil((blockUntilTime - Date.now()) / 1000 / 60)); // 남은 시간(분)
      } else {
        // 차단 시간이 지남
        localStorage.removeItem('codeApplyBlockedUntil');
        setIsBlocked(false);
      }
    }
    
    if (attemptCount) {
      setAttempts(parseInt(attemptCount, 10));
    }

    checkExistingApplications();
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isBlocked) {
      setErrorMessage(`입력 횟수 초과로 ${blockTime}분간 차단되었습니다. 잠시 후 다시 시도해주세요.`);
      setShowErrorModal(true);
      return;
    }

    try {
      setSubmitting(true);
      
      if (!code.trim()) {
        setErrorMessage('코드를 입력해주세요.');
        setShowErrorModal(true);
        setSubmitting(false);
        return;
      }

      // 코드로 신청서 검색
      const applicationsRef = collection(db, 'portville_applications');
      const q = query(
        applicationsRef,
        where('applyCode', '==', code.trim())
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        // 시도 횟수 증가
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        localStorage.setItem('codeApplyAttempts', newAttempts.toString());
        
        // 5회 이상 틀리면 10분 차단
        if (newAttempts >= 5) {
          const blockUntil = Date.now() + 10 * 60 * 1000; // 10분
          localStorage.setItem('codeApplyBlockedUntil', blockUntil.toString());
          setIsBlocked(true);
          setBlockTime(10);
          setErrorMessage('입력 횟수 초과로 10분간 차단되었습니다. 잠시 후 다시 시도해주세요.');
        } else {
          setErrorMessage('유효하지 않은 코드입니다. 다시 확인해주세요.');
        }
        
        setShowErrorModal(true);
        setSubmitting(false);
        return;
      }

      const application = querySnapshot.docs[0];
      const applicationData = application.data();
      
      // UID가 있는지 확인
      if (applicationData.uid && applicationData.uid.trim() !== '') {
        setErrorMessage('이미 사용된 코드입니다. 관리자에게 문의해주세요.');
        setShowErrorModal(true);
        setSubmitting(false);
        return;
      }

      // 사용자 정보로 업데이트
      if (!user) {
        setErrorMessage('로그인이 필요합니다.');
        setShowErrorModal(true);
        setSubmitting(false);
        return;
      }

      // 사용자 정보 가져오기
      const userProfileRef = collection(db, 'user_profiles');
      const userQuery = query(userProfileRef, where('uid', '==', user.uid));
      const userSnapshot = await getDocs(userQuery);
      
      if (userSnapshot.empty) {
        setErrorMessage('사용자 정보를 찾을 수 없습니다.');
        setShowErrorModal(true);
        setSubmitting(false);
        return;
      }

      const userData = userSnapshot.docs[0].data();

      // 문서 업데이트
      await updateDoc(doc(db, 'portville_applications', application.id), {
        uid: user.uid,
        name: userData.name || "",
        email: user.email || "",
        phoneNumber: userData.phoneNumber || "",
        organization: userData.organization || "",
        userLevel: userData.userLevel || 9,
        updatedAt: serverTimestamp()
      });

      // 성공적으로 처리 완료
      // 시도 횟수 초기화
      localStorage.removeItem('codeApplyAttempts');
      localStorage.removeItem('codeApplyBlockedUntil');
      
      // DetailPage로 이동
      navigate('/portville/detail');
    } catch (err) {
      console.error('코드 적용 중 오류 발생:', err);
      setErrorMessage('처리 중 오류가 발생했습니다. 다시 시도해주세요.');
      setShowErrorModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  // 에러 모달 컴포넌트
  const ErrorModal: React.FC = () => {
    if (!showErrorModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <h3 className="text-slate-800 text-xl font-bold font-['Pretendard'] mb-4">오류</h3>
          <p className="text-slate-700 text-base font-normal font-['Pretendard'] mb-6">{errorMessage}</p>
          <div className="flex justify-end">
            <button
              onClick={() => setShowErrorModal(false)}
              className="w-44 h-16 bg-blue-500 hover:bg-blue-600 rounded-sm"
            >
              <div className="text-center text-white text-lg font-bold font-['Pretendard'] leading-9">
                확인
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (authLoading || loading || emailVerificationLoading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-slate-800 text-xl font-normal font-['Pretendard']">로딩 중...</div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="w-full min-h-screen bg-white">
        {/* 본문 컨테이너 - 중앙 정렬 */}
        <div className="w-[760px] mx-auto relative pt-8">
          {/* 헤더 섹션 */}
          <div className="mt-8">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              포트빌 코드 입력
            </div>
            <div className="mt-4 text-slate-700 text-xl font-normal font-['Pretendard'] leading-9">
              기존 오프라인 신청자의 코드입력 페이지입니다
            </div>
          </div>

          {/* 구분선 */}
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>

          {error && (
            <div className="mt-8 bg-red-50 border-l-4 border-red-400 p-4 rounded">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <div className="mt-8">
            {isBlocked ? (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                <p className="text-yellow-700">
                  입력 횟수 초과로 {blockTime}분간 차단되었습니다. 잠시 후 다시 시도해주세요.
                </p>
              </div>
            ) : (
              <div className="bg-gray-50 p-8 rounded border border-black/5">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="code" className="block text-slate-800 text-xl font-bold font-['Pretendard'] mb-4">
                      코드 입력
                    </label>
                    <input
                      type="text"
                      id="code"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-slate-900 text-base font-medium font-['Pretendard'] focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="코드를 입력하세요"
                      disabled={submitting}
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={submitting || isBlocked}
                      className={`w-44 h-16 ${
                        submitting || isBlocked
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-blue-500 hover:bg-blue-600'
                      } rounded-sm`}
                    >
                      <div className="text-center text-white text-lg font-bold font-['Pretendard'] leading-9">
                        {submitting ? '처리 중...' : '코드 확인'}
                      </div>
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          <div className="mt-8 bg-gray-50 p-8 rounded border border-black/5">
            <div className="text-slate-800 text-xl font-bold font-['Pretendard'] mb-4">
              안내사항
            </div>
            <ul className="list-disc list-inside space-y-2 text-slate-700 text-base font-normal font-['Pretendard']">
              <li>잘못된 코드를 5회 이상 입력하면 10분간 코드 입력이 차단됩니다.</li>
              <li>코드를 분실한 경우 관리자에게 문의해주세요.</li>
            </ul>
          </div>

          {/* 하단 여백 */}
          <div className="h-32"></div>
        </div>
      </div>
      
      <ErrorModal />
    </ClientLayout>
  );
};

export default CodeApplyPage; 